/**
 * PredaniFotky — the photo strip of a handover / return dialog (P4-f). The photos are evidence of ONE rental (not the
 * car's gallery) and are taken BEFORE the protocol is written, so they upload immediately into a DRAFT of that side;
 * writing the protocol freezes the set. The strip reads fresh on every open (the signed URLs expire), shows only what
 * a read that finished after the dialog opened says, and tells the dialog when a request is in flight so the dialog
 * neither closes nor submits under it.
 */

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import {
  usePredaniFotkyQuery,
  useRemovePredaniFotoMutation,
  useUploadPredaniFotoMutation,
  type PredaniTyp,
} from './api';

interface PredaniFotkyProps {
  rezervaceId: number;
  typ: PredaniTyp;
  /** The dialog is busy with something else (its own submit): the strip is frozen too. */
  disabled?: boolean;
  /** Reported while an upload / removal is in flight (a read-only strip has none to report). */
  onBusyChange?: (busy: boolean) => void;
  /** The heading; a strip shown for context (a protocol that is already written) says whose photos they are. */
  nadpis?: string;
}

const POPIS: Record<PredaniTyp, string> = {
  vydani: 'Fotografie vozu při předání. Po zapsání protokolu se už nemění.',
  vraceni: 'Fotografie vozu při vrácení. Po zapsání protokolu se už nemění.',
};

export function PredaniFotky({
  rezervaceId,
  typ,
  disabled = false,
  onBusyChange,
  nadpis = 'Fotografie',
}: PredaniFotkyProps) {
  const query = usePredaniFotkyQuery(rezervaceId, typ);
  const upload = useUploadPredaniFotoMutation(rezervaceId, typ);
  const remove = useRemovePredaniFotoMutation(rezervaceId, typ);
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const busy = progress != null || upload.isPending || remove.isPending;
  useEffect(() => {
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);

  const fresh = query.isSuccess && query.isFetchedAfterMount;
  const data = query.data;
  const frozen = disabled || busy;

  async function uploadFiles(list: FileList | null) {
    if (!list || list.length === 0 || frozen) return;
    const images = Array.from(list).filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) {
      toast.warn('Vyberte obrázek — JPG, PNG nebo GIF.');
      return;
    }
    let ok = 0;
    setProgress({ done: 0, total: images.length });
    try {
      for (const [i, file] of images.entries()) {
        try {
          await upload.mutateAsync(file);
          ok += 1;
        } catch (err) {
          toast.error(
            `${file.name}: ${err instanceof Error ? err.message : 'Nepodařilo se nahrát fotografii.'}`,
          );
        }
        setProgress({ done: i + 1, total: images.length });
      }
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = '';
    }
    if (ok > 0) toast.ok(ok === 1 ? 'Fotografie přidána.' : `Přidáno fotografií: ${ok}.`);
  }

  return (
    <section
      data-testid={`predani-fotky-${typ}`}
      className="flex flex-col gap-3 border border-line p-4"
    >
      <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
        {nadpis}
        {data ? ` · ${data.fotky.length}` : ''}
      </div>
      {query.isError ? (
        <p className="text-[13px] text-ink-2">Fotografie se nepodařilo načíst.</p>
      ) : !fresh || !data ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <>
          {data.lze_upravit && (
            <div className="flex flex-wrap items-center gap-3">
              <input
                ref={inputRef}
                data-testid={`predani-fotky-vyber-${typ}`}
                type="file"
                accept="image/jpeg,image/png,image/gif"
                multiple
                className="hidden"
                onChange={(e) => void uploadFiles(e.currentTarget.files)}
              />
              <Button
                size="sm"
                variant="secondary"
                icon={<Icon name="upload" size={12} />}
                disabled={frozen || data.fotky.length >= data.max}
                onClick={() => inputRef.current?.click()}
              >
                {progress
                  ? `Nahrávám ${progress.done + 1} z ${progress.total}…`
                  : 'Přidat fotografie'}
              </Button>
              <span className="font-mono text-[11px] italic text-ink-3">
                JPG, PNG, GIF · max 10 MB · nejvýše {data.max}
              </span>
            </div>
          )}
          {data.fotky.length === 0 ? (
            <p data-testid={`predani-fotky-prazdno-${typ}`} className="text-[13px] text-ink-2">
              Zatím žádná fotografie.
            </p>
          ) : (
            <ul
              data-testid={`predani-fotky-seznam-${typ}`}
              className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
            >
              {data.fotky.map((f) => (
                <li
                  key={f.key}
                  data-testid={`predani-foto-${f.key}`}
                  className="flex flex-col border border-line bg-bg"
                >
                  <a href={f.url} target="_blank" rel="noreferrer">
                    <img
                      src={f.url}
                      alt="Fotografie vozidla"
                      loading="lazy"
                      className="block aspect-[4/3] w-full object-cover"
                    />
                  </a>
                  {data.lze_upravit && (
                    <div className="px-2 py-1.5">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={frozen}
                        onClick={() =>
                          remove.mutate(f.key, {
                            onError: (err) =>
                              toast.error(
                                `Chyba: ${err instanceof Error ? err.message : 'Nepodařilo se odebrat fotografii.'}`,
                              ),
                          })
                        }
                      >
                        Odebrat
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="text-[12px] text-ink-3">{POPIS[typ]}</p>
        </>
      )}
    </section>
  );
}
